/**
 * Widget Font Loader
 *
 * Two non-obvious choices worth keeping in sight:
 *
 * 1. We register fonts via the FontFace API on `document.fonts`, NOT via
 *    `@font-face` injected into the widget's Shadow DOM. Shadow-scoped font
 *    faces are unreliable across browsers (Chrome crbug/336876, Firefox
 *    historical gaps); data-URL faces fail silently with no network request
 *    to debug. `document.fonts` is consulted by all shadow trees at render
 *    time, so the global registration is what shadow children actually use.
 *
 * 2. We do NOT gate on `document.fonts.check()` before adding our face.
 *    `check()` returns true whenever ANY face matches — including a partial
 *    static-cut local install of the same family the user happens to have.
 *    Skipping our load in that case leaves only the partial face registered,
 *    so weights it can't render fall back to system fonts. Always add our
 *    embedded face; the browser picks whichever face matches the requested
 *    weight at render time.
 */

import satoshiVariableBase64 from "../assets/fonts/Satoshi-Variable.woff2";
import { debugLog, warnLog } from "../config";

let loadPromise: Promise<void> | null = null;

export function loadFonts(): Promise<void> {
  if (loadPromise) return loadPromise;

  loadPromise = (async (): Promise<void> => {
    if (typeof document === "undefined" || !("fonts" in document)) {
      warnLog("FontFace API unavailable; falling back to system font.");
      return;
    }

    await Promise.all([
      loadFontFace("Satoshi Variable", satoshiVariableBase64, {
        weight: "300 900",
        style: "normal",
        display: "swap",
      }),
    ]);
  })();

  return loadPromise;
}

async function loadFontFace(
  family: string,
  base64: string,
  descriptors: FontFaceDescriptors,
): Promise<void> {
  try {
    const face = new FontFace(
      family,
      `url(data:font/woff2;base64,${base64}) format("woff2")`,
      descriptors,
    );
    const loaded = await face.load();
    document.fonts.add(loaded);
    debugLog(
      `Font "${family}" loaded (status=${loaded.status}, weight=${loaded.weight}).`,
    );
  } catch (err) {
    warnLog(`Failed to load font "${family}":`, err);
  }
}
